import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SafeImg from '@/components/shared/SafeImg';
import { useIdolOnline } from '@/hooks/useIdolOnline';

/**
 * 偶像頭像：圓形頭像 + 在線綠點（見 docs/idol-online-plan.md §5.1）
 *
 * - 有傳 idolId 才查詢並顯示綠點；沒傳就是純頭像（自己的偶像、管理類列表）
 * - 自己目前使用中的偶像由 useIdolOnline 自動略過
 * - 綠點只有螢幕閱讀器文字，不加 title（不出現 hover 提示）
 * - 綠點大小與位置依 --avatar-size 等比例縮放，樣式在 custom.scss 的 .idol-avatar
 * - 外層固定 flex-shrink-0：放在 flex 列裡時，名稱再長頭像也不會被擠扁
 *
 * @param {String} src - 圖片網址；placeholder 由呼叫端決定
 * @param {'sm'|'sm2'|'md'|'md2'|'lg'} size - 對應 general.scss 的 img-* 尺寸
 * @param {Number} idolId - 要顯示在線狀態的偶像 id
 * @param {String} className - 套在外層（定位、外距等）；可傳 position-absolute 覆蓋預設的 position-relative
 *   （Bootstrap 中 .position-absolute 排在後面，同為 !important 時後者生效，仍可作為綠點的定位基準）
 * @param {String} imgClassName - 套在圖片（例如 border border-white）
 * @param {String} alt - 圖片 alt
 */
function IdolAvatar({ src, size, idolId, className = '', imgClassName = '', alt = '' }) {
  const { t } = useTranslation();
  const isOnline = useIdolOnline(idolId);

  return (
    <div className={`idol-avatar idol-avatar-${size} img-${size} position-relative flex-shrink-0 ${className}`}>
      <SafeImg className={`rounded-pill ${imgClassName}`} src={src} alt={alt} />
      {isOnline && (
        <span className="idol-online-dot position-absolute rounded-circle bg-success">
          <span className="visually-hidden">{t('online')}</span>
        </span>
      )}
    </div>
  );
}

IdolAvatar.propTypes = {
  src: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'sm2', 'md', 'md2', 'lg']).isRequired,
  idolId: PropTypes.number,
  className: PropTypes.string,
  imgClassName: PropTypes.string,
  alt: PropTypes.string,
};

export default IdolAvatar;
